import { Link } from "react-router-dom";
import { ArrowRight, Users, TrendingUp, BarChart3, Eye, Smartphone, MonitorSmartphone, Scissors, Sparkles, Stethoscope, Store, Leaf, HandHeart, MessageCircle, Instagram, ListOrdered } from "lucide-react";
import { whatsappSupportUrl, SUPPORT_INSTAGRAM, SUPPORT_WHATSAPP_DISPLAY } from "../lib/platform";

const WHATSAPP_LINK = whatsappSupportUrl("Olá! Quero conhecer o FILA Gestão.");
const INSTAGRAM_LINK = SUPPORT_INSTAGRAM;

const PLANS = [
  { name: "FILA Essencial", limit: "Até 10 profissionais ativos", setup: "R$ 690", monthly: "R$ 149", highlight: false },
  { name: "FILA Profissional", limit: "11 a 25 profissionais ativos", setup: "R$ 690", monthly: "R$ 249", highlight: true },
  { name: "FILA Gestão", limit: "26 a 50 profissionais ativos", setup: "R$ 690", monthly: "R$ 349", highlight: false },
  { name: "FILA Enterprise", limit: "Acima de 50 profissionais ativos", setup: "R$ 690", monthly: "Sob consulta", highlight: false },
];

const BENEFITS = [
  { icon: Users, title: "Mais organização", desc: "Ordem de atendimento clara para toda a equipe." },
  { icon: TrendingUp, title: "Mais produtividade", desc: "Sem discussão de vez e sem retrabalho na recepção." },
  { icon: BarChart3, title: "Dados para gestão", desc: "Relatórios diários, ranking e histórico auditável." },
  { icon: Eye, title: "Mais transparência", desc: "Painel de TV público exibindo quem está sendo chamado." },
];

const SEGMENTS = [
  { icon: Sparkles, label: "Salões de beleza" },
  { icon: Sparkles, label: "Esmalterias" },
  { icon: Scissors, label: "Barbearias" },
  { icon: Stethoscope, label: "Clínicas" },
  { icon: Store, label: "Estúdios" },
  { icon: Users, label: "Negócios com fila por ordem" },
];

const IMPLANTACAO = [
  "Configuração completa da plataforma",
  "Personalização com nome e logo",
  "Criação de categorias e profissionais",
  "Configuração das regras de atendimento",
  "Treinamento inicial da equipe",
  "Acompanhamento na entrada em operação",
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-zinc-900">
      {/* HEADER */}
      <header className="sticky top-0 z-40 border-b border-brand-100/60 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <a href="/" className="flex items-center gap-3">
            <img src="/fila-gestao-logo.png?v=2" alt="FILA Gestão" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-display text-base font-semibold tracking-tight leading-none">FILA <span className="text-brand-500">Gestão</span></div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Gestão da vez</div>
            </div>
          </a>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="#beneficios" className="text-zinc-600 hover:text-zinc-900">Benefícios</a>
            <a href="#planos" className="text-zinc-600 hover:text-zinc-900">Planos</a>
            <a href="#contato" className="text-zinc-600 hover:text-zinc-900">Contato</a>
          </nav>
          <Link
            to="/entrar"
            data-testid="header-cta-entrar"
            className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-5 py-2 text-xs font-semibold uppercase tracking-widest text-white shadow-sm shadow-brand-500/20 hover:bg-brand-600 hover:-translate-y-[1px] transition-all"
          >
            Entrar
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-grain">
        <div className="pointer-events-none absolute -top-32 -right-20 h-96 w-96 rounded-full bg-brand-100/60 blur-3xl" />
        <div className="pointer-events-none absolute top-1/2 -left-24 h-80 w-80 rounded-full bg-brand-50 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-6 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-brand-500 font-semibold">FILA Gestão</p>
            <h1 className="mt-3 font-display text-5xl sm:text-6xl md:text-7xl font-medium tracking-tight leading-[1.05]" data-testid="landing-hero-title">
              Gestão <span className="text-brand-500">da vez.</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-600 max-w-lg">
              Organiza a ordem de atendimento, gerencia sua equipe e transforma a rotina do seu negócio.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#conheca" data-testid="hero-cta-conhecer" className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-brand-500/20 hover:bg-brand-600 hover:-translate-y-[1px] transition-all">
                Quero conhecer <ArrowRight size={16} />
              </a>
              <Link to="/entrar" data-testid="hero-cta-entrar" className="inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white px-6 py-3 text-sm font-semibold text-zinc-800 hover:border-brand-300 hover:text-brand-600">
                Já sou cliente — Entrar
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="mx-auto max-w-md rounded-3xl border border-brand-100 bg-gradient-to-br from-white to-brand-50 p-8 shadow-lg shadow-brand-500/5">
              <div className="flex items-start gap-4 mb-6">
                <div className="rounded-2xl bg-black text-white p-3"><Smartphone size={22} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Aplicativo</div>
                  <div className="font-semibold">Recepção + Profissional</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="rounded-2xl bg-brand-500 text-white p-3"><MonitorSmartphone size={22} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Painel TV</div>
                  <div className="font-semibold">Chamada com áudio automática</div>
                </div>
              </div>
              <div className="mt-8 rounded-2xl bg-white border border-brand-100 p-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Próxima da vez</div>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold">J</div>
                  <div><div className="font-semibold">Juliana</div><div className="text-xs text-zinc-500">Manicure · Preferência</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONHEÇA */}
      <section id="conheca" className="border-y border-zinc-200 bg-white py-20">
        <div className="mx-auto max-w-6xl px-6">
          <p className="text-center text-xs uppercase tracking-[0.3em] text-brand-500 font-semibold">Conheça o FILA Gestão</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-medium tracking-tight text-center">Como funciona no dia a dia</h2>
          <p className="mt-3 text-center text-zinc-600 max-w-2xl mx-auto">Uma plataforma completa para organizar a ordem de atendimento — do momento em que o cliente chega até o relatório para gestão.</p>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="rounded-2xl border border-zinc-200 bg-white p-6 hover:border-brand-200 hover:shadow-sm hover:shadow-brand-500/10 transition-all">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-500 mb-4">
                <Users size={20} />
              </div>
              <div className="font-semibold text-lg">Recepção</div>
              <div className="mt-1 text-sm text-zinc-600">A recepção registra a chegada, atribui o serviço e a fila se organiza automaticamente por categoria e regras do estabelecimento.</div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-6 hover:border-brand-200 hover:shadow-sm hover:shadow-brand-500/10 transition-all">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-500 mb-4">
                <Smartphone size={20} />
              </div>
              <div className="font-semibold text-lg">Aplicativo do profissional</div>
              <div className="mt-1 text-sm text-zinc-600">Cada profissional acompanha sua posição na fila pelo celular, recebe o aviso da vez e registra pausas com um toque — sem depender da recepção.</div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-6 hover:border-brand-200 hover:shadow-sm hover:shadow-brand-500/10 transition-all">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-500 mb-4">
                <MonitorSmartphone size={20} />
              </div>
              <div className="font-semibold text-lg">Painel TV</div>
              <div className="mt-1 text-sm text-zinc-600">A tela pública anuncia com voz automática quem está sendo chamado. Transparência total, sem discussões de vez.</div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-6 hover:border-brand-200 hover:shadow-sm hover:shadow-brand-500/10 transition-all">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-500 mb-4">
                <ListOrdered size={20} />
              </div>
              <div className="font-semibold text-lg">Organização da vez</div>
              <div className="mt-1 text-sm text-zinc-600">Regras claras de rotatividade, preferências, pausas e prioridades — a fila anda sozinha, do jeito que o seu negócio precisa.</div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-6 hover:border-brand-200 hover:shadow-sm hover:shadow-brand-500/10 transition-all">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-500 mb-4">
                <BarChart3 size={20} />
              </div>
              <div className="font-semibold text-lg">Relatórios & gestão</div>
              <div className="mt-1 text-sm text-zinc-600">Histórico auditável, ranking por profissional, exportação e filtros por período — informação para decidir com dados, não com achismo.</div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-6 hover:border-brand-200 hover:shadow-sm hover:shadow-brand-500/10 transition-all">
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-500 mb-4">
                <Eye size={20} />
              </div>
              <div className="font-semibold text-lg">Transparência</div>
              <div className="mt-1 text-sm text-zinc-600">Cliente vê quem está sendo chamado, profissional vê sua posição e o gestor acompanha tudo em tempo real. Sem papel, sem retrabalho.</div>
            </div>
          </div>

          <div className="mt-12 flex justify-center">
            <a
              href="#planos"
              data-testid="conheca-cta-ver-planos"
              className="inline-flex items-center gap-2 rounded-full bg-brand-500 px-8 py-3.5 text-sm font-semibold uppercase tracking-widest text-white shadow-sm shadow-brand-500/20 hover:bg-brand-600 hover:-translate-y-[1px] transition-all"
            >
              Ver planos <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="beneficios" className="border-y border-zinc-200 bg-zinc-50/60 py-20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-center mb-12">Por que sua equipe adora</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {BENEFITS.map((b, i) => (
              <div key={i} className="rounded-2xl bg-white border border-zinc-200 p-6 hover:border-brand-200 hover:shadow-sm hover:shadow-brand-500/10 transition-all">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-500 mb-4">
                  <b.icon size={20} />
                </div>
                <div className="font-semibold text-lg">{b.title}</div>
                <div className="mt-1 text-sm text-zinc-600">{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARA QUEM */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-center mb-4">Para quem é o FILA Gestão</h2>
          <p className="text-center text-zinc-600 mb-12 max-w-2xl mx-auto">Qualquer negócio que trabalha com ordem de atendimento.</p>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {SEGMENTS.map((s, i) => (
              <div key={i} className="flex flex-col items-center rounded-xl border border-zinc-200 bg-white p-5 text-center hover:border-brand-200">
                <s.icon size={22} className="text-brand-500 mb-2" />
                <div className="text-sm font-medium">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANOS */}
      <section id="planos" className="bg-gradient-to-b from-brand-50/60 to-white py-24 border-y border-brand-100/60">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-center mb-4">
            Planos e <span className="text-brand-500">Investimento</span>
          </h2>
          <p className="text-center text-zinc-600 mb-12 max-w-2xl mx-auto">Escolha pelo porte da sua equipe. Implantação inclusa em todos os planos.</p>
          <div className="grid md:grid-cols-4 gap-5">
            {PLANS.map((p, i) => (
              <div
                key={i}
                data-testid={`plan-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                className={`relative rounded-2xl p-6 border transition-all ${p.highlight ? "border-brand-500 bg-white shadow-xl shadow-brand-500/15 md:-translate-y-2" : "border-zinc-200 bg-white hover:border-brand-200"}`}
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-500 px-3 py-1 text-[10px] uppercase tracking-widest text-white font-semibold shadow-sm shadow-brand-500/20">
                    Mais escolhido
                  </div>
                )}
                <div className="font-display text-lg font-semibold">
                  FILA <span className="text-brand-500">{p.name.replace("FILA ", "")}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1">{p.limit}</div>
                <div className="mt-6 rounded-xl bg-brand-50/60 border border-brand-100 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Implantação</div>
                  <div className="font-semibold text-zinc-900">{p.setup} <span className="text-[10px] font-normal text-zinc-500">Pagamento único</span></div>
                </div>
                <div className="mt-3 rounded-xl bg-brand-500 text-white p-4">
                  <div className="text-[10px] uppercase tracking-widest text-white/80">Mensalidade</div>
                  <div className="font-display text-2xl font-semibold leading-tight">{p.monthly}</div>
                  <div className="text-[10px] text-white/80">por mês</div>
                </div>
                <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer"
                   className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-widest transition-all ${p.highlight ? "bg-black text-white hover:bg-brand-500" : "border border-brand-200 text-brand-600 hover:bg-brand-50"}`}>
                  Falar com o time
                </a>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-zinc-500 mt-8">*Planos comerciais. Não há bloqueio automático por plano nesta fase — a operação é liberada após a implantação.</p>
        </div>
      </section>

      {/* IMPLANTAÇÃO */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-4">Implantação sem dor de cabeça</h2>
            <p className="text-zinc-600">Nossa equipe entra em campo com você e deixa tudo rodando.</p>
          </div>
          <ul className="space-y-3">
            {IMPLANTACAO.map((item, i) => (
              <li key={i} className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-white p-4 hover:border-brand-200">
                <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-500 text-white text-xs font-bold">{i + 1}</span>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* IMPACTO */}
      <section className="border-y border-brand-100/60 bg-gradient-to-b from-white to-brand-50/40 py-20">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10">
          <div className="rounded-2xl bg-white border border-brand-100 p-8 shadow-sm shadow-brand-500/5">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-500 mb-4"><Leaf size={20} /></div>
            <h3 className="font-display text-2xl font-medium tracking-tight mb-2">Impacto Ambiental</h3>
            <p className="text-sm text-zinc-600">Ao substituir listas em papel e planilhas impressas por um fluxo 100% digital, cada operação evita centenas de folhas por mês, reduz descarte e simplifica auditoria — sem prejuízo da transparência.</p>
          </div>
          <div className="rounded-2xl bg-white border border-brand-100 p-8 shadow-sm shadow-brand-500/5">
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-500 mb-4"><HandHeart size={20} /></div>
            <h3 className="font-display text-2xl font-medium tracking-tight mb-2">Impacto Social</h3>
            <p className="text-sm text-zinc-600">Menos discussões na recepção, mais equidade na distribuição da vez, e uma jornada de trabalho mais leve para as profissionais. O painel público dá transparência a clientes e reduz atritos no dia a dia.</p>
          </div>
        </div>
      </section>

      {/* CONTATO */}
      <section id="contato" className="py-20 bg-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-4">Vamos organizar sua fila</h2>
          <p className="text-zinc-600 mb-8">Fale com o time comercial e vamos entender o seu negócio.</p>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noreferrer"
            data-testid="cta-whatsapp"
            className="inline-flex items-center gap-3 rounded-full bg-brand-500 px-8 py-4 text-white font-semibold shadow-lg shadow-brand-500/20 hover:bg-brand-600 hover:-translate-y-[1px] transition-all"
          >
            <MessageCircle size={18} /> Quero conhecer o FILA Gestão
          </a>
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-zinc-600">
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-brand-600"><MessageCircle size={14} /> {SUPPORT_WHATSAPP_DISPLAY}</a>
            <a href={INSTAGRAM_LINK} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 hover:text-brand-600"><Instagram size={14} /> @filagestao</a>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200 py-8 text-center text-xs text-zinc-500">
        FILA Gestão · Plataforma multi-estabelecimento · Desenvolvido por <span className="font-medium text-zinc-700">Elite Tecnologia</span> · © {new Date().getFullYear()}
      </footer>
    </div>
  );
}
